import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../core/services/auth.service';
import { MatDialog } from '@angular/material/dialog';

// Bestimmt den im Header links angezeigten Abschnittsnamen anhand des URL-Prefixes - je
// spezifischer, desto weiter oben in der Liste (erster Treffer gewinnt).
const SECTION_TITLES: Array<[string, string]> = [
  ['/admin/cards', 'Admin · Karten'],
  ['/admin/packs', 'Admin · Packs'],
  ['/admin', 'Admin'],
  ['/decks', 'Deckeditor'],
  ['/matchrooms', 'Spielen'],
  ['/cards', 'Kartendatenbank'],
  ['/inventory', 'Inventar'],
  ['/packs', 'Inventar'],
  ['/shop', 'Pack Shop'],
  ['/coins', 'Coins kaufen'],
];

@Component({
  selector: 'fw-app-header',
  standalone: true,
  imports: [RouterLink, MatToolbarModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
})
export class AppHeaderComponent {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  readonly auth = inject(AuthService);

  private readonly currentUrl = signal(this.router.url);

  readonly sectionTitle = computed(() => {
    const url = this.currentUrl();
    const match = SECTION_TITLES.find(([prefix]) => url.startsWith(prefix));
    return match?.[1] ?? '';
  });

  readonly showBack = computed(() => this.currentUrl() !== '/' && !!this.auth.user());

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.currentUrl.set(this.router.url);
    });
  }

  // Dynamischer Import statt statischem, damit das Dialog-Formular (Reactive Forms, Dialog-
  // Modul) nicht Teil des initialen Bundles wird - der App-Header ist immer eager geladen.
  async editBalance(): Promise<void> {
    if (!this.auth.isAdmin()) return;
    const { SetCoinsDialogComponent } = await import('../set-coins-dialog/set-coins-dialog.component');
    const currentBalance = this.auth.user()?.coinBalance ?? 0;
    const dialogRef = this.dialog.open(SetCoinsDialogComponent, { data: { currentBalance } });
    dialogRef.afterClosed().subscribe((newBalance?: number) => {
      if (newBalance !== undefined && newBalance !== null) {
        this.auth.setCoinBalance(newBalance).subscribe();
      }
    });
  }

  logout(): void {
    this.auth.logout().subscribe({
      complete: () => this.router.navigate(['/login']),
      error: () => this.router.navigate(['/login']),
    });
  }
}
